import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { IconRefresh } from '@tabler/icons-react';
import PlanCard from '../components/PlanCard';
import AgentsFlowService from '../../../services/AgentsFlowService';

const CAPABILITY_LABELS = {
  scheduling: 'Agendamento',
  faq: 'Perguntas Frequentes',
  data_collection: 'Captura de Dados',
  media_library: 'Galeria de Mídias',
  media_analysis: 'Análise de Mídias',
};

const PlanningStep = ({ wizardState, setWizardState }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [error, setError] = useState(null);

  const { idealizationSummary, userPlan, technicalPlan } = wizardState;
  const iteration = wizardState._planIteration || 0;

  // Auto-trigger on first entry
  useEffect(() => {
    if (!userPlan && !isLoading && idealizationSummary) {
      generatePlan(false);
    }
  }, []);

  const generatePlan = async (isAdjust = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await AgentsFlowService.wizardPlan({
        idealization_summary: idealizationSummary,
        iteration: isAdjust ? iteration + 1 : 0,
        previous_plan: isAdjust ? { user_plan: userPlan, technical_plan: technicalPlan } : null,
        adjustment_notes: isAdjust ? adjustmentNotes : '',
      });

      setWizardState((prev) => ({
        ...prev,
        userPlan: response.data.user_plan,
        technicalPlan: response.data.technical_plan,
        _planIteration: isAdjust ? iteration + 1 : 0,
        _planApproved: false,
      }));
      setShowAdjustment(false);
      setAdjustmentNotes('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar planejamento. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    setWizardState((prev) => ({
      ...prev,
      _planApproved: true,
    }));
  };

  if (isLoading && !userPlan) {
    return (
      <div className="awc-loading" aria-live="polite">
        <div className="awc-loading__icon">
          <Spinner animation="border" />
        </div>
        <p className="awc-loading__text">A IA está planejando seu agente...</p>
        <p className="awc-loading__subtext">
          Analisando as melhores capacidades para o seu negócio
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="awc-body__title">Plano do seu agente</h3>
      <p className="awc-body__subtitle">
        A IA preparou um plano personalizado. Revise e aprove.
      </p>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            className="awc-btn awc-btn--link"
            onClick={() => generatePlan(false)}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {userPlan && (
        <>
          {userPlan.map((item) => (
            <PlanCard
              key={item.step}
              step={item.step}
              title={item.title}
              description={item.description}
              icon={item.icon}
            />
          ))}

          {technicalPlan && (
            <div className="awc-summary mt-3">
              <h4 className="awc-summary__title">Configuração técnica</h4>
              <div className="row g-2 mt-1">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Capacidades:</strong></p>
                  <ul className="awc-summary__list">
                    {(technicalPlan.capabilities || []).map((cap, i) => (
                      <li key={i}>{CAPABILITY_LABELS[cap] || cap.replace('_', ' ')}</li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>Intenções detectadas:</strong></p>
                  <ul className="awc-summary__list">
                    {(technicalPlan.suggested_graph_structure?.entry_conditions || []).map((cond, i) => (
                      <li key={i}>{cond.label}</li>
                    ))}
                  </ul>
                </div>
              </div>
              {technicalPlan.needs_knowledge_base && (
                <p className="mt-2 mb-0 awc-text--warning">
                  Este plano recomenda uma base de conhecimento (FAQ)
                </p>
              )}
            </div>
          )}

          {!wizardState._planApproved && (
            <div className="d-flex gap-2 justify-content-end mt-3">
              {iteration < 2 && (
                <button
                  className="awc-btn awc-btn--secondary"
                  onClick={() => setShowAdjustment(!showAdjustment)}
                  disabled={isLoading}
                >
                  <IconRefresh size={16} />
                  Ajustar
                </button>
              )}
              <button
                className="awc-btn awc-btn--success"
                onClick={handleApprove}
              >
                Aprovar plano
              </button>
            </div>
          )}

          {wizardState._planApproved && (
            <div className="alert alert-success mt-3">
              Plano aprovado. Avance para o próximo passo.
            </div>
          )}

          {showAdjustment && (
            <div className="awc-refinement mt-3">
              <label className="awc-refinement__label">
                O que gostaria de ajustar no plano?
              </label>
              <textarea
                className="awc-textarea awc-textarea--sm"
                placeholder="Ex: Não preciso de agendamento, foque mais em FAQ e captura de leads..."
                value={adjustmentNotes}
                onChange={(e) => setAdjustmentNotes(e.target.value)}
              />
              <div className="mt-2 text-end">
                <button
                  className="awc-btn awc-btn--primary awc-btn--sm"
                  onClick={() => generatePlan(true)}
                  disabled={isLoading || !adjustmentNotes.trim()}
                >
                  {isLoading ? 'Ajustando...' : 'Aplicar ajuste'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PlanningStep;
